import { SubmissionState } from "@/api/submission-types";
import { ValidationAction, ValidationAssetType } from "@/api/validation-types";
import { RejectWholeEpisodeDialog } from "@/components/episodes/RejectWholeEpisodeDialog";
import { ImageDetails } from "@/components/images/ImageDetails";
import { MaskDetails } from "@/components/images/MaskDetails";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { BoxLoader } from "@/components/ui/loaders";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { NextEpisodeDialog } from "@/components/validation/NextEpisodeDialog";
import { RejectionDialog } from "@/components/validation/RejectionDialog";
import { ValidationControls } from "@/components/validation/ValidationControls";
import { useIsChildPresent } from "@/hooks/use-is-child-present";
import { useRejectWholeEpisode } from "@/hooks/use-reject-whole-episode";
import { useImageById } from "@/queries/images/use-image-by-id";
import { useAssetsReadyForValidation } from "@/queries/validation/use-assets-ready-for-validation";
import { useValidateImage } from "@/queries/validation/use-validate-image";
import { useValidateMask } from "@/queries/validation/use-validate-mask";
import { Box, Typography } from "@mui/material";
import { useState } from "react";

export function ValidationPage() {
  const { data, isLoading, error } = useAssetsReadyForValidation();

  const [index, setIndex] = useState(0);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [nextEpisodeDialogOpen, setNextEpisodeDialogOpen] = useState(false);

  const asset = data[index];

  const { data: imageData } = useImageById(
    asset?.type === ValidationAssetType.image ? asset.assetId : undefined,
  );

  const isCheckboxDisabled =
    imageData?.submissionState !== SubmissionState.unsubmitted;

  const {
    isChildPresent: isChildPresented,
    toggleIsChildPresent: toggleCharacter,
    error: changeIsChildPresentedError,
  } = useIsChildPresent(
    asset?.assetId || "",
    isCheckboxDisabled || asset?.type !== ValidationAssetType.image,
  );

  const { mutate: validateImage, error: validateImageError } =
    useValidateImage();
  const { mutate: validateMask, error: validateMaskError } = useValidateMask();

  const {
    confirmRejectWholeEpisode,
    setConfirmRejectWholeEpisode,
    handleRejectWholeEpisode: handleRejectWholeEpisodeBase,
    isRejectingWholeEpisode,
    canRejectEpisode,
  } = useRejectWholeEpisode({
    episodeId: asset?.episodeId || "",
    shouldNavigateToNext: false,
    onSuccess: () => {
      // Move to next asset not in the same episode
      const nextAsset = data.find(
        (a, idx) => idx > index && a.episodeId !== asset?.episodeId,
      );
      if (nextAsset) {
        const nextIndex = data.findIndex(
          (a) => a.assetId === nextAsset.assetId,
        );
        setIndex(nextIndex);
      } else {
        setIndex(data.length); // No more assets
      }
    },
  });

  const validateFn =
    asset?.type === ValidationAssetType.image
      ? validateImage
      : asset?.type === ValidationAssetType.mask
        ? validateMask
        : () => {};

  function checkNextEpisodeDialog(next = true) {
    const nextAsset = data[next ? index + 1 : index - 1];
    if (nextAsset && nextAsset.episodeId !== asset.episodeId) {
      setNextEpisodeDialogOpen(true);
    }
  }

  function onValidateSuccess() {
    checkNextEpisodeDialog();
    setIndex((index) => index + 1);
  }

  const accept = () => {
    validateFn(
      { id: asset.assetId, action: ValidationAction.accept },
      {
        onSuccess: onValidateSuccess,
      },
    );
  };

  const goNext = () => {
    checkNextEpisodeDialog();
    setIndex((prevIndex) => Math.min(prevIndex + 1, data.length - 1));
  };

  const goPrev = () => {
    checkNextEpisodeDialog(false);
    setIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const reject = (reasons: string[], notes?: string) => {
    validateFn(
      {
        id: asset.assetId,
        action: ValidationAction.reject,
        rejectionReasons: reasons,
        notes,
      },
      {
        onSuccess: onValidateSuccess,
        onSettled: () => {
          setRejectionDialogOpen(false);
        },
      },
    );
  };

  const handleRejectWholeEpisode = (notes?: string) => {
    if (!asset) {
      return;
    }
    handleRejectWholeEpisodeBase(notes);
  };

  return (
    <Box marginBottom={13}>
      <ApiErrorAlert error={error} sx={{ marginBottom: 2 }} />
      <ApiErrorAlert error={validateImageError} sx={{ marginBottom: 2 }} />
      <ApiErrorAlert error={validateMaskError} sx={{ marginBottom: 2 }} />
      <ApiErrorSnackbar error={changeIsChildPresentedError} />
      <BoxLoader isLoading={isLoading} height={100} />
      {!asset && !isLoading && !error && (
        <Typography>{"No more assets to validate!"}</Typography>
      )}
      {asset && asset.type === ValidationAssetType.image && (
        <ImageDetails
          mode={"validate"}
          id={asset.assetId}
          isChildPresent={isChildPresented}
          onToggleCharacter={toggleCharacter}
          onRejectWholeEpisode={
            canRejectEpisode
              ? () => setConfirmRejectWholeEpisode(true)
              : undefined
          }
        />
      )}
      {asset && asset.type === ValidationAssetType.mask && (
        <MaskDetails
          mode={"validate"}
          id={asset.assetId}
          onRejectWholeEpisode={
            canRejectEpisode
              ? () => setConfirmRejectWholeEpisode(true)
              : undefined
          }
        />
      )}
      <RejectionDialog
        open={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        onReject={reject}
        asset={asset}
      />
      {asset && (
        <ValidationControls
          disabled={
            rejectionDialogOpen ||
            nextEpisodeDialogOpen ||
            confirmRejectWholeEpisode
          }
          onReject={() => setRejectionDialogOpen(true)}
          onAccept={accept}
          showNextPrev
          onNext={goNext}
          onPrev={goPrev}
          showCharacterToggle={asset.type === ValidationAssetType.image}
          onToggleCharacter={toggleCharacter}
        />
      )}
      <NextEpisodeDialog
        open={nextEpisodeDialogOpen}
        onClose={() => setNextEpisodeDialogOpen(false)}
      />
      <RejectWholeEpisodeDialog
        open={confirmRejectWholeEpisode}
        onClose={() => setConfirmRejectWholeEpisode(false)}
        onConfirm={handleRejectWholeEpisode}
        isLoading={isRejectingWholeEpisode}
      />
    </Box>
  );
}
